import { useQuery } from "react-query";
import axios from "axios";
import default_help_text from "./default_help.txt";

const PREVIEW_URL_TOKEN = "{{REACT_APP_PREVIEW_URL}}";

/**
 * Fetches the default help text from a local text file and replaces the preview URL token 
 * with the actual environment variable value.
 * 
 * @returns {Promise<Object>} An object containing the parsed help text data.
 */
export const fetchDefaultHelpText = () => (
  axios.get(default_help_text).then((response) => ({
    ...response,
    data: response.data
      .split(PREVIEW_URL_TOKEN)
      .join(process.env.REACT_APP_PREVIEW_URL || ""),
  }))
);

/**
 * A React Query hook that fetches and caches the default help text.
 * Uses React Suspense for loading states.
 * 
 * @returns {string} The parsed default help text string.
 */
export const useDefaultHelpTextQuery = () => {
  const { data } = useQuery("defaultHelpText", fetchDefaultHelpText, {
    suspense: true,
  });
  return data.data;
};

/**
 * Default branding payload for PUT /games/:game_id/grey.json (reset branding).
 * Matches API flat parameter names and default values.
 */
export const getDefaultBrandingPayload = () => {
  const mainMenuDefaults = [
    { key: "challenges", visible: true, order: 0 },
    { key: "stream", visible: true, order: 1 },
    { key: "leaderboard", visible: true, order: 2 },
    { key: "npcs", visible: false, order: 3 },
    { key: "reels", visible: false, order: 4 },
    { key: "rewards", visible: true, order: 5 },
    { key: "join", visible: true, order: 6 },
    { key: "notifications", visible: true, order: 7 },
    { key: "help", visible: true, order: 8 },
    { key: "custom", visible: false, order: 9 },
  ];

  const payload = {
    top_nav_background_color: null,
    top_nav_txt_color: null,
    top_nav_text_color: null,
    top_nav_tint_color: null,
    body_high_color: null,
    body_highlight_color: null,
    body_txt_color: null,
    body_text_color: null,
    body_background_color: null,
    body_tint_color: null,
    font_size: 16,
    font_regular: null,
    font_bold: null,
    logo_media_uuid: null,
    logo_media_extension: null,
    help: null,
    terms: null,
    theme: 1,
    timer_visible: 1,
    watermark_uuid: null,
    watermark_extension: null,
  };

  const iconPrefixByKey = {
    challenges: "challengesenabled",
    stream: "streamenabled",
    leaderboard: "leaderboardenabled",
    npcs: "npcsenabled",
    reels: "reelsenabled",
    rewards: "rewardsenabled",
    join: "joinenabled",
    notifications: "notificationsenabled",
    help: "helpenabled",
    custom: "customenabled",
  };

  mainMenuDefaults.forEach(({ key, visible, order }) => {
    const iconPrefix = iconPrefixByKey[key];
    payload[`${key}_visible`] = visible;
    payload[`${key}_order`] = order;
    payload[`${key}_title`] = null;
    payload[`${key}_subtitle`] = null;
    payload[`${iconPrefix}_media_uuid`] = null;
    payload[`${iconPrefix}_media_extension`] = null;
  });
  payload.custom_url = null;

  [1, 2, 3, 4].forEach((i) => {
    payload[`tour_${i}_media_uuid`] = null;
    payload[`tour_${i}_media_extension`] = null;
  });

  return {
    ...payload,
    ...getClearedBrandingAssets(),
  };
};

/** Icon names for main menu items (used in assets.icons). */
const BRANDING_ICON_NAMES = [
  "challenges",
  "stream",
  "leaderboard",
  "npcs",
  "reels",
  "rewards",
  "join",
  "notifications",
  "help",
  "custom",
];

const DEFAULT_ICON_DIMENSIONS = { width: 2000, height: 2000 };

/**
 * Cleared assets.icons for branding reset (all main menu icons with url: null).
 * Included in getDefaultBrandingPayload so the mutation can update the game cache.
 */
export const getClearedBrandingAssets = () => ({
  assets: {
    photos: [
      {
          "name": "standard",
          "url": "https://ss-assets.socialscavenger.com/images/game.png",
          "width": 1024,
          "height": 1024
      },
      {
          "name": "logo",
          "url": null,
          "width": 512,
          "height": 412
      }
    ],
    icons: BRANDING_ICON_NAMES.map((name) => ({
      name,
      url: null,
      ...DEFAULT_ICON_DIMENSIONS,
    })),
  },
});
